import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getCommaSeparatedValues } from "@WhiteCap/Common/Utillities/utils";
import ScrollToTopButton from "@WhiteCap/Components/OSNSubscriptions/ScrollToTopButton";
import useDebounce from "@WhiteCap/CustomHooks/useDebounce";
import loadAllJobSites from "@WhiteCap/Handlers/SubscriptionsDashboard/GetAllJobSites";
import loadSubscriptionsData from "@WhiteCap/Handlers/SubscriptionsDashboard/GetSubscriptionsData";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { SiteSummaryModel } from "@WhiteCap/Store/Service/GetAllJobSitesService";
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: SubscriptionsModalStyles;
    isModalOpen: boolean;
    closeModalHandler: (value: boolean) => void;
    viewOnly: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const subscriptionsData =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData?.jobSiteSubscriptionsById[currentSubscriptionCustomerId];
    const { searchedJobSites, jobSites, searchTerm } = state.WhiteCap.subscriptionsDashboardData;
    return {
        subscriptionsData,
        notificationType: state.WhiteCap.subscriptionsDashboardData.osnType,
        currentSubscriptionCustomerId: state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId,
        jobSitesCount: subscriptionsData?.totalSites,
        session: state.context.session,
        billTosById: state.data.billTos.byId,
        shipTosById: state.data.shipTos.byId,
        jobSites: searchTerm ? searchedJobSites : jobSites,
        isFetchingJobSites: state.WhiteCap.subscriptionsDashboardData.isFetchingJobSites,
        searchTerm,
        fetchJobSitesError: state.WhiteCap.subscriptionsDashboardData.fetchJobSitesError,
    };
};

const mapDispatchToProps = {
    loadAllJobSites,
    loadSubscriptionsData,
};

export interface SubscriptionsModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    body?: InjectableCss;
    radioGroup?: RadioGroupProps;
    radioButton?: RadioProps;
    footer?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    proceedButton?: ButtonPresentationProps;
    gridItem?: GridItemProps;
    sectionHeading?: TypographyProps;
    jobSiteContainer?: InjectableCss;
    jobSiteSection?: InjectableCss;
    section1?: InjectableCss;
    section2?: InjectableCss;
    section3?: InjectableCss;
    companyName?: TypographyProps;
    address?: TypographyProps;
    sectionName?: TypographyProps;
    sectionValue?: TypographyProps;
    mainInfoWrapper?: InjectableCss;
    modalHeadline?: TypographyProps;
    headlineWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    searchTextField?: TextFieldPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    jobSitesContainer?: InjectableCss;
    scrollToTopContainer?: InjectableCss;
    noResults?: TypographyProps;
}

export const subscriptionsModalStyles: SubscriptionsModalStyles = {
    modal: {
        size: 1161,
        cssOverrides: {
            modalTitle: css`
                align-items: start;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 18px 15px;
                            `,
                            null,
                            css`
                                padding: 28px 33px;
                            `,
                        ],
                        "min",
                    );
                }}
                [class^="TypographyStyle"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 16px;
                                    font-weight: 400;
                                    line-height: 25px;
                                `,
                                null,
                                css`
                                    font-size: 20px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
            modalContent: css`
                padding: 0;
                display: flex;
                flex-direction: column;
                height: -webkit-fill-available;
                overflow: hidden;
            `,
            modalContainer: css`
                [class^="ContentBody"] {
                    overflow: hidden;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    margin: 0 45px;
                                    height: 90vh;
                                    max-height: 846px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
            `,
        },
        closeButtonProps: {
            size: 24,
            css: css`
                padding: 0;
                [class^="IconWrapper"] {
                    height: 20px;
                    width: 20px;
                    svg {
                        height: 20px;
                        width: 20px;
                    }
                }
            `,
        },
    },
    modalHeadline: {
        weight: 400,
        lineHeight: "25px",
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                        `,
                        null,
                        css`
                            font-size: 20px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },

    container: { gap: 20 },

    radioGroup: {
        css: css`
            margin-left: 0 !important;
            display: flex;
            flex-direction: row;
            legend {
                display: none;
            }
            & > ${RadioStyle} {
                margin-bottom: 0;
                line-height: normal;
                margin-top: 0px;
                display: flex;
                input {
                    display: contents;
                }
                label {
                    cursor: pointer;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    margin-left: 0px;
                                    margin-top: 4px;
                                    height: fit-content;
                                `,
                                null,
                                css`
                                    margin-left: -35px;
                                    margin-top: 0;
                                    height: auto;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
                input:checked + label::after {
                    content: "";
                    display: block;
                    height: 6px;
                    width: 6px;
                    border-radius: 50%;
                    background: black;
                    position: absolute;
                    left: 5px;
                    top: calc(50% - 3px);
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-left: 0;
                        `,
                        null,
                        css`
                            margin-left: 38px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    radioButton: {
        css: css`
            margin-bottom: 18px;
            input {
                margin-left: 0;
                width: 100%;
            }
        `,
    },
    footer: {
        css: css`
            display: flex;
            gap: 20px;
            padding: 15px;
            justify-content: center;
            align-items: center;
            box-shadow: 0px -4px 6px 0px rgba(0, 0, 0, 0.1);
            z-index: 1;
        `,
    },

    cancelButton: {
        variant: "secondary",
    },
    proceedButton: {
        variant: "primary",
    },
    gridItem: {
        width: 12,
        css: css`
            padding: 0;
            margin: 0;
        `,
    },
    sectionHeading: {
        variant: "h4",
        weight: 325,
        lineHeight: "normal",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            margin-bottom: 8px;
                        `,
                        null,
                        css`
                            font-size: 24px;
                            margin-bottom: 21px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    jobSiteContainer: {
        css: css`
            display: grid;
            border-bottom: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-areas:
                                "header"
                                "section2"
                                "section3"
                                "section4"
                                "section5";
                            padding: 10px 12px;
                        `,
                        null,
                        css`
                            grid-template-areas:
                                "header header header header"
                                "section2 section3 section4 section5";
                            padding: 10px 60px;
                        `,
                    ],
                    "min",
                )}

            gap: 10px;
            .section2 {
                grid-area: section2;
            }
            .section3 {
                grid-area: section3;
            }
            .section4 {
                grid-area: section4;
            }
            .section5 {
                grid-area: section5;
            }
        `,
    },
    jobSiteSection: {
        css: css`
            display: flex;
            gap: 7px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: row;
                            flex-wrap: wrap;
                        `,
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    section1: {
        css: css`
            grid-area: header;
            display: flex;
        `,
    },
    companyName: {
        weight: 450,
        lineHeight: "20px",
        css: css`
            text-transform: capitalize;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                            &::after {
                                content: " - ";
                                white-space: pre;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    address: {
        weight: 325,
        lineHeight: "20px",
        css: css`
            text-transform: capitalize;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    sectionName: {
        weight: 325,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 10px;
                            &::after {
                                content: " - ";
                            }
                        `,
                        null,
                        css`
                            font-size: 12px;
                            &::after {
                                content: "";
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    sectionValue: {
        weight: 450,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 10px;
                        `,
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    mainInfoWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            margin-bottom: 3px;
                        `,
                        null,
                        css`
                            flex-direction: row;
                            margin-bottom: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headlineWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        css`
                            flex-direction: row;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    searchTextField: {
        cssOverrides: {
            formInputWrapper: css`
                max-width: 509px;
                > input {
                    height: 35px;
                    padding-right: 40px !important;
                }
            `,
        },
    },
    searchWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 12px 12px;
                        `,
                        null,
                        css`
                            padding: 10px 33px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    body: {
        css: css`
            flex-grow: 1;
            overflow: hidden;
            flex-direction: column;
            display: flex;
            position: relative;
            .selected {
                background: #f7f8f9;
            }
            .selectable {
                cursor: pointer;
            }
        `,
    },
    jobSitesContainer: {
        css: css`
            flex: 1;
            overflow: auto;
        `,
    },
    scrollToTopContainer: {
        css: css`
            position: absolute;
            bottom: 10px;
            left: 50%;
            transform: translate(-50%, 0);
        `,
    },
    noResults: {
        variant: "h3",
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            margin-top: 20px;
        `,
    },
};

const styles = subscriptionsModalStyles;

type JobSiteProps = {
    jobSite: SiteSummaryModel;
    selectedJobSite: string;
    jobSiteSelectionHandler: (value: string) => void;
    viewOnly: boolean;
};

const JobSite = forwardRef(
    ({ jobSite, selectedJobSite, jobSiteSelectionHandler, viewOnly }: JobSiteProps, ref: any) => {
        const address =
            getCommaSeparatedValues([
                jobSite.address1,
                jobSite.address2,
                jobSite.city,
                jobSite.state,
                jobSite.postalCode,
            ])?.toLocaleLowerCase() || "";

        return (
            <StyledWrapper
                {...styles.jobSiteContainer}
                className={!viewOnly ? `selectable ${selectedJobSite === jobSite.id ? "selected" : ""}` : ""}
                onClick={() => jobSiteSelectionHandler(jobSite.id)}
                ref={ref}
            >
                <StyledWrapper {...styles.section1}>
                    {!viewOnly && (
                        <RadioGroup {...styles.radioGroup} value={selectedJobSite}>
                            <Radio {...styles.radioButton} value={jobSite.id} />
                        </RadioGroup>
                    )}
                    <StyledWrapper {...styles.mainInfoWrapper}>
                        <Typography {...styles.companyName}>{jobSite.companyName?.toLowerCase()}</Typography>
                        <Typography {...styles.address}>{address}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.jobSiteSection} className="section2">
                    <Typography {...styles.sectionName}>Sales Order Acknowledgement (SOA)</Typography>
                    <Typography {...styles.sectionValue}>{`${jobSite.soaSubscriberCount} ${
                        jobSite.isPrintPriceActive ? "" : `(Do not print price)`
                    }`}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.jobSiteSection} className="section3">
                    <Typography {...styles.sectionName}>Advanced Shipping Notice (ASN)</Typography>
                    <Typography {...styles.sectionValue}>{jobSite.asnSubscriberCount}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.jobSiteSection} className="section4">
                    <Typography {...styles.sectionName}>Order Complete & Proof Of Delivery (POD)</Typography>
                    <Typography {...styles.sectionValue}>{`${jobSite.ocSubscriberCount || 0}`
                    }</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.jobSiteSection} className="section5">
                    <Typography {...styles.sectionName}>Proof Of Delivery Reporting</Typography>
                    <Typography {...styles.sectionValue}>{`${jobSite.podSubscriberCount} ${
                        jobSite.frequency && `(${jobSite.frequency})`
                    }`}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    },
);

const allJobSitesModal = ({
    isModalOpen,
    closeModalHandler,
    currentSubscriptionCustomerId,
    loadAllJobSites,
    jobSitesCount,
    session,
    billTosById,
    jobSites,
    viewOnly,
    loadSubscriptionsData,
    notificationType,
    isFetchingJobSites,
    searchTerm,
    fetchJobSitesError,
}: Props) => {
    if (!session.billToId || !session.shipToId) {
        return null;
    }

    const [inProgress, setInProgress] = useState(false);
    const billToId = session.billToId;
    const shipToId = session.shipToId;
    const billToDetails = billTosById[billToId];
    const [selectedJobSite, setSelectedJobSite] = useState(currentSubscriptionCustomerId);
    const [pageSize] = useState(16);
    const debouncedSearchTerm = useDebounce(searchTerm, 400);
    const toasterContext = useContext(ToasterContext);

    const {
        pagination: { totalItemCount, page },
    } = jobSites;
    const dispatch = useDispatch();
    const noSearchResultsMessage = siteMessage("OSN_No_Job_Site_Search_Results_Found_Message");

    const handleSelect = () => {
        setInProgress(true);
        dispatch({
            type: "Custom/SubscriptionDashboard/ChangeNotificationType",
            value: "individualJobSiteLevel",
        });
        loadSubscriptionsData({
            customerId: selectedJobSite,
            currentSessionShipToId: billToId,
            currentSessionBillToId: shipToId,
            onSuccess: () => {
                setInProgress(true);
                closeModalHandler(false);
                loadAllJobSites({
                    customerId: selectedJobSite,
                    page: 1,
                    pageSize,
                    onError: (message: string) => {
                        toasterContext.addToast({
                            body: message,
                            messageType: "danger",
                        });
                    },
                });
            },
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
    };

    useEffect(() => {
        if (!currentSubscriptionCustomerId) {
            return;
        }
        loadAllJobSites({
            customerId: currentSubscriptionCustomerId,
            page: 1,
            pageSize,
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
        return () => {
            dispatch({
                type: "Custom/SubscriptionDashboard/UpdateSearchTerm",
                value: "",
            });
        };
    }, []);

    useEffect(() => {
        if (debouncedSearchTerm) {
            loadAllJobSites({
                customerId: selectedJobSite,
                page: 1,
                pageSize,
                filter: debouncedSearchTerm,
                onError: (message: string) => {
                    toasterContext.addToast({
                        body: message,
                        messageType: "danger",
                    });
                },
            });
        }
        scrollTop();
    }, [debouncedSearchTerm]);

    const jobSiteSelectionHandler = (value: string) => {
        setSelectedJobSite(value);
    };

    const SearchTextChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: "Custom/SubscriptionDashboard/UpdateSearchTerm",
            value: e.target.value,
        });
    };

    const clearSearchField = () => {
        dispatch({
            type: "Custom/SubscriptionDashboard/UpdateSearchTerm",
            value: "",
        });
        scrollTop();
    };

    const scrollTop = () => {
        const element = document.getElementById("job-sites-container");
        if (element) {
            element.scrollTo({ top: 0 });
        }
    };

    const headline = (
        <StyledWrapper {...styles.headlineWrapper}>
            <Typography {...styles.modalHeadline}>Jobsite(s) for account #</Typography>
            <Typography {...styles.modalHeadline}>
                {`${billToDetails?.customerNumber || ""} ${billToDetails?.properties?.customerName || ""} ${
                    notificationType === "accountLevel" ? jobSitesCount || "" : ""
                }`}
            </Typography>
        </StyledWrapper>
    );

    const observer: React.MutableRefObject<any> = useRef();

    const itemsLeft = totalItemCount - page * pageSize;

    const infiniteScrollTriggerRef = useCallback(
        node => {
            if (getIsWebCrawler() || isFetchingJobSites || fetchJobSitesError) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
            }

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && itemsLeft > 0) {
                    searchTerm && debouncedSearchTerm
                        ? loadAllJobSites({
                              customerId: currentSubscriptionCustomerId,
                              page: page + 1,
                              pageSize,
                              filter: debouncedSearchTerm,
                              onError: (message: string) => {
                                  toasterContext.addToast({
                                      body: message,
                                      messageType: "danger",
                                  });
                              },
                          })
                        : loadAllJobSites({
                              customerId: currentSubscriptionCustomerId,
                              page: page + 1,
                              pageSize,
                              onError: (message: string) => {
                                  toasterContext.addToast({
                                      body: message,
                                      messageType: "danger",
                                  });
                              },
                          });
                }
            });

            if (node) {
                observer.current?.observe(node);
            }
        },
        [isFetchingJobSites, itemsLeft],
    );

    return (
        <Modal {...styles.modal} headline={headline} isOpen={isModalOpen} handleClose={() => closeModalHandler(false)}>
            <StyledWrapper {...styles.body}>
                <StyledWrapper {...styles.searchWrapper}>
                    <TextField
                        {...styles.searchTextField}
                        type="text"
                        onChange={SearchTextChangeHandler}
                        value={searchTerm}
                        placeholder="Search by Jobsite name, City, State, Zip Code"
                        maxLength={256}
                        iconProps={{
                            src: searchTerm ? X : Search,
                        }}
                        iconClickableProps={{
                            onClick: () => {
                                clearSearchField();
                            },
                        }}
                    />
                </StyledWrapper>

                <StyledWrapper {...styles.jobSitesContainer} id="job-sites-container">
                    {jobSites?.sitesSummary?.map((jobSite: SiteSummaryModel, ind: number) => {
                        return jobSites?.sitesSummary && ind === jobSites?.sitesSummary?.length - 3 ? (
                            <JobSite
                                jobSite={jobSite}
                                key={jobSite.id}
                                selectedJobSite={selectedJobSite}
                                jobSiteSelectionHandler={jobSiteSelectionHandler}
                                viewOnly={viewOnly}
                                ref={infiniteScrollTriggerRef}
                            />
                        ) : (
                            <JobSite
                                jobSite={jobSite}
                                key={jobSite.id}
                                selectedJobSite={selectedJobSite}
                                jobSiteSelectionHandler={jobSiteSelectionHandler}
                                viewOnly={viewOnly}
                            />
                        );
                    })}
                    {jobSites?.sitesSummary?.length === 0 && (
                        <Typography {...styles.noResults}>{noSearchResultsMessage}</Typography>
                    )}
                </StyledWrapper>
                {isFetchingJobSites && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.scrollToTopContainer}>
                    <ScrollToTopButton containerId="job-sites-container" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.footer}>
                <Button
                    {...styles.cancelButton}
                    onClick={() => closeModalHandler(false)}
                    data-test-selector="cancelButton"
                >
                    {translate("Cancel")}
                </Button>
                {!viewOnly && jobSitesCount && jobSitesCount > 0 ? (
                    <Button {...styles.proceedButton} onClick={handleSelect} disabled={inProgress}>
                        {translate("Select")}
                    </Button>
                ) : null}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(allJobSitesModal);
